/**
 * posSyncWorker — drains the sync_outbox table to Firestore on reconnect.
 *
 * Triggers:
 *   - On app start (one-shot)
 *   - On 'online' event from connectivity.js
 *   - Periodic 30 s tick while online (catches dropped ops)
 *   - Manual trigger from UI ("Sync now" button)
 *
 * For each pending outbox row:
 *   - Push the bundle (transaction + subTransactions + stockFlow) via a
 *     Firestore writeBatch (atomic per sale).
 *   - On success: mark outbox row 'synced' and update local rows with the
 *     cloud's IDs (so the next sync-down doesn't create duplicates).
 *   - On failure: mark 'failed', increment retries, back off exponentially.
 *
 * Multi-tab safety: a tiny lock in localStorage prevents two tabs from
 * draining the same row simultaneously. (Best effort — race is benign at
 * worst since each row's outcome is idempotent: either it pushed or it'll
 * retry.)
 */

import { collection, doc, getDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { posPoolIncrements, markZeroCostPosRows, commitPoolUpdates } from './itemCostEngine';
import {
  listPendingOutbox, markOutboxSynced, markOutboxFailed,
  countPendingOutbox, cleanupSyncedOutbox, resetStuckOutboxRetries
} from './posLocalCache';
import { dexieDb } from '../dataStore/dexieSchema';
import { isOnline, onConnectivityChange } from './connectivity';

// ─── Sync lock (cross-tab via localStorage) ─────────────────────────────

const LOCK_KEY = 'amal.posSync.lock';
const LOCK_TTL_MS = 30 * 1000;

const tryAcquireLock = () => {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (raw) {
      const lock = JSON.parse(raw);
      if (lock.expiresAt > Date.now()) return false; // someone else holds it
    }
    localStorage.setItem(LOCK_KEY, JSON.stringify({
      holder: Math.random().toString(36).slice(2),
      acquiredAt: Date.now(),
      expiresAt: Date.now() + LOCK_TTL_MS
    }));
    return true;
  } catch { return true; } // localStorage broken — proceed anyway
};

const releaseLock = () => { try { localStorage.removeItem(LOCK_KEY); } catch { /* ignore */ } };

// ─── Listener bus for sync status (used by UI badge) ────────────────────

const _statusListeners = new Set();
export const onSyncWorkerStatus = (cb) => { _statusListeners.add(cb); return () => _statusListeners.delete(cb); };
const notifyStatus = (status) => { for (const l of _statusListeners) { try { l(status); } catch { /* ignore */ } } };

// ─── Per-row push to Firestore ──────────────────────────────────────────

// Extra documents a queued sale may carry (anything else is ignored).
const EXTRA_WRITE_COLLECTIONS = ['bankDiscountLog'];

const pushBundle = async (companyId, bundle) => {
  const { transaction, subTransactions = [], stockFlow = [] } = bundle;
  if (!transaction) throw new Error('Outbox bundle missing transaction');

  // Create the transaction with the SAME id we used locally (so cloud and
  // local agree). Firestore accepts arbitrary string IDs.
  const txnRef = doc(db, 'companies', companyId, 'transactions', transaction.id);

  // Already in the cloud (an earlier try was saved but its reply was lost):
  // nothing to do — never overwrite it.
  const existing = await getDoc(txnRef);
  if (existing.exists()) return;

  const batch = writeBatch(db);
  // Strip local-only marker fields before writing to cloud
  const cleanTxn = { ...transaction };
  delete cleanTxn._companyId;
  delete cleanTxn._offlineCreated;
  delete cleanTxn._offlineCreatedAt;
  batch.set(txnRef, cleanTxn);

  for (const sub of subTransactions) {
    const r = doc(db, 'companies', companyId, 'subTransactions', sub.id);
    const clean = { ...sub };
    delete clean._companyId;
    batch.set(r, clean);
  }
  const cleanStock = stockFlow.map((stock) => {
    const clean = { ...stock };
    delete clean._companyId;
    return clean;
  });
  // INV-04: same as an online POS sale — zero-cost lines wait for a cost and
  // the items' running averages move in this same batch.
  markZeroCostPosRows(cleanStock);
  cleanStock.forEach((clean, i) => {
    batch.set(doc(db, 'companies', companyId, 'stockFlow', stockFlow[i].id), clean);
  });
  // POS-01: a sale queued after a failed online save also carries its
  // bank-discount report row (keyed by the sale's id) — same batch as the sale.
  const extraWrites = (Array.isArray(bundle.extraWrites) ? bundle.extraWrites : [])
    .filter(w => w && EXTRA_WRITE_COLLECTIONS.includes(w.collection) && w.id && w.data);
  extraWrites.forEach((w) => {
    batch.set(doc(db, 'companies', companyId, w.collection, w.id), w.data);
  });
  const poolUpdates = posPoolIncrements(companyId, cleanStock, transaction.financialYearId);
  // A very large bill: keep under Firestore's 500-writes-per-save limit.
  const poolInMain = 1 + subTransactions.length + cleanStock.length + extraWrites.length + poolUpdates.length <= 480;
  if (poolInMain) poolUpdates.forEach(u => batch.update(u.ref, u.data));

  await batch.commit();
  if (!poolInMain) await commitPoolUpdates(poolUpdates);
};

// ─── Drain loop ─────────────────────────────────────────────────────────

let _draining = false;

/**
 * Drain the outbox.
 * @param {object} [opts]
 * @param {boolean} [opts.force]  When true (user clicked "Sync now"), reset
 *   retries on stuck rows first so back-off doesn't keep silently skipping
 *   them. Auto/periodic drains pass force=false to respect back-off.
 */
export const drainOutbox = async (opts = {}) => {
  const force = !!opts.force;
  console.log('[posSyncWorker] drainOutbox called', {
    force,
    online: isOnline(),
    navigatorOnline: typeof navigator !== 'undefined' ? navigator.onLine : null,
    draining: _draining,
    hasAuth: !!auth?.currentUser,
    authUid: auth?.currentUser?.uid || null
  });

  if (_draining) {
    console.log('[posSyncWorker] skip — already draining');
    return { ok: false, reason: 'already-draining' };
  }
  if (!isOnline()) {
    console.log('[posSyncWorker] skip — offline');
    return { ok: false, reason: 'offline' };
  }
  // If the user is offline-authenticated (logged in via cached credentials
  // with no real Firebase token), Firestore writes will be rejected by the
  // security rules. Surface this clearly instead of silently failing each row.
  if (!auth?.currentUser) {
    const pendingCount = await countPendingOutbox();
    if (pendingCount > 0) {
      console.warn(
        '[posSyncWorker] cannot sync: no Firebase Auth session. ' +
        `You have ${pendingCount} pending sale(s). ` +
        'Re-login while online to refresh the session, then sync will resume automatically.'
      );
    }
    return { ok: false, reason: 'no-firebase-auth-token' };
  }
  if (!tryAcquireLock()) {
    console.log('[posSyncWorker] skip — lock held by other tab');
    return { ok: false, reason: 'lock-held-by-other-tab' };
  }

  _draining = true;
  notifyStatus({ phase: 'draining', startedAt: new Date().toISOString() });

  let synced = 0;
  let failed = 0;

  try {
    // Force drain: reset retries on stuck rows so they get a fresh attempt
    if (force) {
      const resetCount = await resetStuckOutboxRetries();
      if (resetCount > 0) {
        console.log(`[posSyncWorker] force-drain: reset retries on ${resetCount} stuck row(s)`);
      }
    }

    const pending = await listPendingOutbox();
    console.log(`[posSyncWorker] draining ${pending.length} pending row(s)`);
    if (pending.length === 0) {
      notifyStatus({ phase: 'completed', synced: 0, failed: 0, pendingRemaining: 0 });
      return { ok: true, synced: 0, failed: 0, reason: 'nothing-pending' };
    }

    for (const row of pending) {
      // Exponential back-off: skip rows with too many recent retries
      // (unless force=true reset them above)
      if (row.retries >= 5) {
        console.warn(
          `[posSyncWorker] skip outbox #${row.seq} — too many retries (${row.retries}). ` +
          `Last error: ${row.lastError || 'unknown'}. ` +
          `Click "Sync now" to force-retry.`
        );
        continue;
      }
      try {
        await pushBundle(row.companyId, row.bundle);
        await markOutboxSynced(row.seq);
        synced++;
        console.log(`[posSyncWorker] synced outbox #${row.seq} (${row.kind || 'sale'})`);
      } catch (err) {
        await markOutboxFailed(row.seq, err.message);
        failed++;
        console.warn(`[posSyncWorker] push failed for outbox #${row.seq}:`, err?.code || err?.message, err);
      }
    }

    // House-keeping
    await cleanupSyncedOutbox(7);

    const pendingRemaining = await countPendingOutbox();
    console.log(`[posSyncWorker] drain complete: ${synced} synced, ${failed} failed, ${pendingRemaining} remaining`);
    notifyStatus({
      phase: 'completed',
      finishedAt: new Date().toISOString(),
      synced, failed,
      pendingRemaining
    });
    return { ok: true, synced, failed };
  } catch (err) {
    console.error('[posSyncWorker] drain crashed:', err);
    notifyStatus({ phase: 'error', error: err });
    return { ok: false, error: err };
  } finally {
    _draining = false;
    releaseLock();
  }
};

// ─── Background scheduler ───────────────────────────────────────────────

let _started = false;
let _intervalId = null;

export const startSyncWorker = () => {
  if (_started) return;
  _started = true;

  // Run once at start
  drainOutbox().catch(() => { /* errors already logged */ });

  // Subscribe to connectivity changes
  onConnectivityChange((online) => {
    if (online) drainOutbox().catch(() => {});
  });

  // Periodic tick — every 30 s while online
  _intervalId = setInterval(() => {
    if (isOnline()) drainOutbox().catch(() => {});
  }, 30 * 1000);
};

export const stopSyncWorker = () => {
  _started = false;
  if (_intervalId) { clearInterval(_intervalId); _intervalId = null; }
};

// Live pending count for the UI badge
export const getPendingCount = async () => countPendingOutbox();
